import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import { message } from 'antd'

import { handleError } from '../../../../../lib/utils'
import { countBookings, readBookingsAggregate } from '../../../../../Admin/bookings/utils/utils'
import { readSystemUsersAggregate } from '../../../../../Admin/system-users/utils/utils'
import { readPaymentMethods } from '../../../../../Admin/entities/utils/utils'
import EmployeeBookingsReducer from '../../../../utils/employee-bookings-reducer'
import BillingReducer from '../../../../utils/billing-reducer'
import CoreEnums from '../../../../../../core/utils/enums'

// Define statusesOptions outside the hook as it does not depend on props or state from within the hook
const STATUSES_OPTIONS = [
  CoreEnums.bookingStatuses.checkin,
  CoreEnums.bookingStatuses.screening,
  CoreEnums.bookingStatuses.consultation,
  CoreEnums.bookingStatuses.reception
]

const useBookingsData = ({
  userRef,
  isBilling,
  viewingPatientProfile,
  rangeFilter,
  additionalFilters,
  setAdditionalFilters, // Make sure this is passed from the main component
  searchQuery,
  filteredValues,
  page,
  pageSize,
  setPage, // To reset page on search/filter change,
  facilityFilter
}) => {
  const { data: clinics } = useSelector((state) => state.clinics)

  // Granular selectors for authState parts
  const agiliteUserId = useSelector((state) => state.auth?.agiliteUser?._id)
  const agiliteUserRoleType = useSelector((state) => state.auth?.agiliteUser?.extraData?.role?.type)
  const agiliteUserProfession = useSelector((state) => state.auth?.agiliteUser?.extraData?.profession)
  const agiliteUserClinics = useSelector((state) => state.auth?.agiliteUser?.extraData?.clinics) // This is an array

  const servicesData = useSelector((state) => state.services?.data || [])
  const virtualServicesData = useSelector((state) => state.virtualServices?.data || [])
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [currentUserData, setCurrentUserData] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [services, setServices] = useState([])

  useEffect(() => {
    setServices([...servicesData, ...virtualServicesData])
  }, [servicesData, virtualServicesData])

  // Memoize stringified versions of object/array dependencies for useCallback
  const additionalFiltersStr = useMemo(() => JSON.stringify(additionalFilters), [additionalFilters])
  const rangeFilterStr = useMemo(() => JSON.stringify(rangeFilter), [rangeFilter])
  const filteredValuesStr = useMemo(() => JSON.stringify(filteredValues), [filteredValues])
  const agiliteUserClinicsStr = useMemo(() => JSON.stringify(agiliteUserClinics), [agiliteUserClinics])

  const handleGetAllBookings = useCallback(async () => {
    let tmpBookings = []
    let tmpUsers = []
    let tmpUserIds = []
    let qry = {}
    let userQuery = null
    let tmpTotal = 0
    // Parse back from string for actual use, original references are used in dependencies
    let currentFilteredValues = JSON.parse(filteredValuesStr || '{}')
    let currentAdditionalFilters = JSON.parse(additionalFiltersStr || '{}')
    let currentRangeFilter = JSON.parse(rangeFilterStr || '[]')
    let currentAgiliteUserClinics = JSON.parse(agiliteUserClinicsStr || '[]')

    try {
      setLoading(true)

      if (searchQuery) {
        const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/)
        const regexPatterns = searchTerms.map((term) => new RegExp(term, 'i'))
        userQuery = {
          $or: [
            {
              $expr: {
                $regexMatch: {
                  input: { $concat: [{ $toLower: '$firstName' }, ' ', { $toLower: '$lastName' }] },
                  regex: searchTerms.map((term) => `.*${term}.*`).join(''),
                  options: 'i'
                }
              }
            },
            {
              $or: [
                {
                  $expr: {
                    $regexMatch: {
                      input: { $toLower: '$firstName' },
                      regex: regexPatterns.join('|'),
                      options: 'i'
                    }
                  }
                },
                {
                  $expr: {
                    $regexMatch: {
                      input: { $toLower: '$lastName' },
                      regex: regexPatterns.join('|'),
                      options: 'i'
                    }
                  }
                }
              ]
            },
            { phoneNumber: { $regex: searchQuery, $options: 'i' } },
            { email: { $regex: searchQuery, $options: 'i' } },
            { physicalAddress: { $regex: searchQuery, $options: 'i' } },
            { idNo: { $regex: searchQuery, $options: 'i' } }
          ]
        }
      }

      if (currentAdditionalFilters.nonScheduledOnly) {
        qry.$or = [{ bookingDate: { $exists: false } }, { bookingDate: null }]
      } else if (currentRangeFilter && currentRangeFilter[0] && currentRangeFilter[1]) {
        qry.bookingDate = {
          $gt: dayjs(currentRangeFilter[0]).format('YYYY-MM-DDT00:00:00.000Z'),
          $lt: dayjs(currentRangeFilter[1]).add(1, 'day').format('YYYY-MM-DDT00:00:00.000Z')
        }
      }

      if (!viewingPatientProfile) {
        if (agiliteUserRoleType === 'medical_professional') {
          if (agiliteUserProfession === 'doctor') {
            qry.medicalProfRef = { $in: [agiliteUserId, null] }
          }
          if (agiliteUserProfession === 'nurse') {
            qry.clinicRef = { $in: currentAgiliteUserClinics }
          }
        }
        if (agiliteUserRoleType === 'reception') {
          qry.clinicRef = { $in: currentAgiliteUserClinics }
        }
      }

      for (const prop in currentFilteredValues) {
        if (currentFilteredValues[prop] && currentFilteredValues[prop].length > 0) {
          const noShowIndex = currentFilteredValues[prop].indexOf('No Show')
          if (noShowIndex > -1) {
            qry.wasNoShow = true
            currentFilteredValues[prop].splice(noShowIndex, 1)
          }
        }
        if (currentFilteredValues[prop] && currentFilteredValues[prop].length > 0) {
          qry[prop] = { $in: currentFilteredValues[prop] }
        }
      }

      if (currentAdditionalFilters?.status?.length > 0) {
        let finalStatuses = [...currentAdditionalFilters.status]
        if (!currentAdditionalFilters.hideCancelled) {
          finalStatuses.push(CoreEnums.bookingStatuses.cancelled)
        }
        if (!currentAdditionalFilters.hideCompleted) {
          finalStatuses.push(CoreEnums.bookingStatuses.completed)
        }
        // TODO: Uncomment this when we have a way to filter by statusq
        // qry.status = { $in: [...finalStatuses, ''] }
      } else {
        let finalStatuses = [...STATUSES_OPTIONS]
        if (!currentAdditionalFilters.hideCancelled) {
          finalStatuses.push(CoreEnums.bookingStatuses.cancelled)
        }
        if (!currentAdditionalFilters.hideCompleted) {
          finalStatuses.push(CoreEnums.bookingStatuses.completed)
        }
        // TODO: Uncomment this when we have a way to filter by status
        // qry.status = { $in: [...finalStatuses, ''] }
      }

      if (currentAdditionalFilters?.type?.length > 0) {
        if (!currentAdditionalFilters.type.includes('standard')) {
          qry.isVirtual = true
        }
      }
      if (currentAdditionalFilters?.service?.length > 0) {
        qry.service = { $in: currentAdditionalFilters.service }
      }
      if (userRef) {
        qry.userRef = userRef
      }
      if (isBilling) {
        qry.status = CoreEnums.bookingStatuses.reception
      }

      if (facilityFilter && facilityFilter !== '') {
        qry.clinicRef = { $in: [facilityFilter] }
      } else {
        if (agiliteUserRoleType === 'admin') {
          qry.clinicRef = { $in: clinics.map((i) => i._id) }
        }
      }

      const [userSearchResult, countResult, paymentMethodsResult] = await Promise.all([
        searchQuery ? readSystemUsersAggregate(userQuery, null, null, '_id', true) : Promise.resolve([]),
        countBookings(qry),
        readPaymentMethods()
      ])

      if (searchQuery) {
        tmpUsers = userSearchResult
        tmpUserIds = tmpUsers.map((user) => user._id)
        if (tmpUserIds.length > 0) {
          qry.$or = [{ userRef: { $in: tmpUserIds } }, { medicalProfRef: { $in: tmpUserIds } }]
        }
      }

      tmpTotal = countResult
      if (tmpTotal > 0 || (searchQuery && tmpUserIds.length > 0) || !searchQuery) {
        // Ensure we try to fetch if count > 0 OR if there was a user search with results OR if there was no search query (to fetch initial/filtered data)
        tmpBookings = await readBookingsAggregate(qry, page, pageSize, {
          sort: { bookingDate: -1, startTime: 1 }
        })
      } else {
        tmpBookings = [] // Ensure tmpBookings is an empty array if not fetched
      }

      setPaymentMethods(paymentMethodsResult)

      tmpBookings.forEach((entry) => {
        if (entry.isVirtualVisit) {
          entry.serviceRecord = virtualServicesData.find((s) => s._id === entry.service)
        } else {
          entry.serviceRecord = servicesData.find((s) => s._id === entry.service)
        }
      })

      if (userRef) {
        setCurrentUserData(tmpBookings)
      } else {
        if (isBilling) {
          dispatch(BillingReducer.actions.setRecords(tmpBookings))
        } else {
          dispatch(EmployeeBookingsReducer.actions.setRecords(tmpBookings))
        }
      }
      setTotal(tmpTotal)
    } catch (e) {
      message.error(handleError(e, true))
      console.error('[useBookingsData] Error in handleGetAllBookings:', e)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line
  }, [
    searchQuery,
    page,
    pageSize,
    userRef,
    isBilling,
    viewingPatientProfile,
    agiliteUserId,
    agiliteUserRoleType,
    agiliteUserProfession,
    agiliteUserClinicsStr,
    dispatch,
    servicesData,
    virtualServicesData,
    additionalFiltersStr,
    rangeFilterStr,
    filteredValuesStr,
    facilityFilter
  ])

  useEffect(() => {
    handleGetAllBookings()
  }, [handleGetAllBookings]) // Re-run when the callback itself changes due to its dependencies

  return {
    loading,
    total,
    currentUserData,
    paymentMethods,
    services,
    handleGetAllBookings // Expose for refresh button
  }
}

export default useBookingsData
